import React, { useState, useRef, useCallback, useMemo } from 'react';

interface AgeSliderProps {
    value: number;
    onChange: (age: number) => void;
    'aria-labelledby'?: string;
}

const AgeSlider: React.FC<AgeSliderProps> = ({ value, onChange, 'aria-labelledby': ariaLabelledBy }) => {
    const ages = useMemo(() => Array.from({ length: 41 }, (_, i) => 40 + i), []); // [40, 41, 42, ..., 79, 80]
    const [isDragging, setIsDragging] = useState(false);
    const sliderRef = useRef<HTMLDivElement>(null);
    const currentIndex = ages.indexOf(value);

    const updateSliderPosition = useCallback((index: number) => {
        const percentage = index / (ages.length - 1);
        const sliderTrack = sliderRef.current;
        if (!sliderTrack) return;

        const trackWidth = sliderTrack.offsetWidth;
        const thumb = sliderTrack.querySelector('.slider-thumb') as HTMLElement;
        const fill = sliderTrack.querySelector('.slider-fill') as HTMLElement;
        const ageDisplay = sliderTrack.querySelector('.age-display') as HTMLElement;

        // Get actual thumb width from DOM
        const thumbWidth = thumb ? thumb.offsetWidth : 54;

        // Update thumb position
        const thumbPosition = percentage * (trackWidth - thumbWidth);

        if (thumb) {
            thumb.style.left = thumbPosition + 'px';
        }
        if (fill) {
            // Fill from left to right - since ages go from 80 to 40, 
            // higher percentage means closer to 40 (right side)
            fill.style.width = (percentage * trackWidth) + 'px';
            fill.style.left = '0';
            fill.style.right = 'auto';
        }
        if (ageDisplay) {
            ageDisplay.style.left = thumbPosition + (thumbWidth / 2) + 'px';
        }
    }, [ages.length]);

    const handleTrackClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const trackWidth = rect.width;

        // Calculate position as percentage
        const percentage = Math.max(0, Math.min(1, clickX / trackWidth));

        // Find closest age index
        const index = Math.round(percentage * (ages.length - 1));
        const newIndex = Math.max(0, Math.min(ages.length - 1, index));

        onChange(ages[newIndex]);
        updateSliderPosition(newIndex);
    }, [ages, onChange, updateSliderPosition]);

    const handleTrackTouch = useCallback((e: React.TouchEvent<HTMLDivElement>) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const touch = e.touches[0];
        const clickX = touch.clientX - rect.left;
        const trackWidth = rect.width;

        // Calculate position as percentage
        const percentage = Math.max(0, Math.min(1, clickX / trackWidth));

        // Find closest age index
        const index = Math.round(percentage * (ages.length - 1));
        const newIndex = Math.max(0, Math.min(ages.length - 1, index));

        onChange(ages[newIndex]);
        updateSliderPosition(newIndex);
    }, [ages, onChange, updateSliderPosition]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        setIsDragging(true);
        if (sliderRef.current && sliderRef.current.parentElement) {
            sliderRef.current.parentElement.classList.add('dragging');
        }
        e.preventDefault();
    }, []);

    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        setIsDragging(true);
        if (sliderRef.current && sliderRef.current.parentElement) {
            sliderRef.current.parentElement.classList.add('dragging');
        }
        e.preventDefault();
    }, []);

    const handleMouseMove = useCallback((e: MouseEvent) => {
        if (!isDragging || !sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const trackWidth = rect.width;

        // Calculate position as percentage
        const percentage = Math.max(0, Math.min(1, clickX / trackWidth));

        // Find closest age index
        const index = Math.round(percentage * (ages.length - 1));
        const newIndex = Math.max(0, Math.min(ages.length - 1, index));

        onChange(ages[newIndex]);
        updateSliderPosition(newIndex);
    }, [isDragging, ages, onChange, updateSliderPosition]);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (!isDragging || !sliderRef.current) return;

        const rect = sliderRef.current.getBoundingClientRect();
        const touch = e.touches[0];
        const clickX = touch.clientX - rect.left;
        const trackWidth = rect.width;

        // Calculate position as percentage
        const percentage = Math.max(0, Math.min(1, clickX / trackWidth));

        // Find closest age index
        const index = Math.round(percentage * (ages.length - 1));
        const newIndex = Math.max(0, Math.min(ages.length - 1, index));

        onChange(ages[newIndex]);
        updateSliderPosition(newIndex);
    }, [isDragging, ages, onChange, updateSliderPosition]);

    const handleMouseUp = useCallback(() => {
        setIsDragging(false);
        if (sliderRef.current && sliderRef.current.parentElement) {
            sliderRef.current.parentElement.classList.remove('dragging');
        }
    }, []);

    const handleTouchEnd = useCallback(() => {
        setIsDragging(false);
        if (sliderRef.current && sliderRef.current.parentElement) {
            sliderRef.current.parentElement.classList.remove('dragging');
        }
    }, []);

    const [inputValue, setInputValue] = useState(value.toString());

    const handleInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // If it's a valid number in range, update immediately
        const numValue = parseInt(newValue, 10);
        if (!isNaN(numValue) && numValue >= 40 && numValue <= 80) {
            onChange(numValue);
        }
    }, [onChange]);

    const handleInputBlur = useCallback(() => {
        const numValue = parseInt(inputValue, 10);
        if (!isNaN(numValue) && numValue >= 40 && numValue <= 80) {
            onChange(numValue);
        } else {
            // Reset to current value if invalid
            setInputValue(value.toString());
        }
    }, [inputValue, value, onChange]);

    const handleInputFocus = useCallback(() => {
        // Select all text when input is focused for easier editing on touch devices
        const input = document.querySelector('.age-input-field') as HTMLInputElement;
        if (input) {
            input.select();
        }
    }, []);

    const handleInputKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            const numValue = parseInt(inputValue, 10);
            if (!isNaN(numValue) && numValue >= 40 && numValue <= 80) {
                onChange(numValue);
            } else {
                // Reset to current value if invalid
                setInputValue(value.toString());
            }
        }

        // Allow: backspace, delete, tab, escape, enter, home, end, left, right, up, down
        if ([8, 9, 27, 13, 46, 35, 36, 37, 38, 39, 40].indexOf(e.keyCode) !== -1 ||
            // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
            (e.keyCode === 65 && e.ctrlKey === true) ||
            (e.keyCode === 67 && e.ctrlKey === true) ||
            (e.keyCode === 86 && e.ctrlKey === true) ||
            (e.keyCode === 88 && e.ctrlKey === true)) {
            return;
        }
        // Allow numbers (0-9) from both main keyboard and numpad
        if ((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 96 && e.keyCode <= 105)) {
            return;
        }
        // Block everything else
        e.preventDefault();
    }, [inputValue, value, onChange]);


    // Add event listeners for mouse and touch events
    React.useEffect(() => {
        if (isDragging) {
            document.addEventListener('mousemove', handleMouseMove);
            document.addEventListener('mouseup', handleMouseUp);
            document.addEventListener('touchmove', handleTouchMove);
            document.addEventListener('touchend', handleTouchEnd);
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.removeEventListener('touchmove', handleTouchMove);
            document.removeEventListener('touchend', handleTouchEnd);
        };
    }, [isDragging, handleMouseMove, handleMouseUp, handleTouchMove, handleTouchEnd]);

    // Initialize slider position
    React.useEffect(() => {
        updateSliderPosition(currentIndex);
    }, [currentIndex, updateSliderPosition]);

    // Update input value when slider value changes
    React.useEffect(() => {
        setInputValue(value.toString());
    }, [value]);

    return (
        <div className="slider-container">
            <div
                className="slider-track"
                ref={sliderRef}
                onClick={handleTrackClick}
                onTouchStart={handleTrackTouch}
                role="slider"
                aria-label={ariaLabelledBy ? undefined : "גיל"}
                aria-labelledby={ariaLabelledBy}
                aria-valuemin={40}
                aria-valuemax={80}
                aria-valuenow={value}
                aria-valuetext={`${value} שנים`}
                tabIndex={-1}
            >
                <div className="slider-fill" aria-hidden="true"></div>
                <div
                    className="slider-thumb"
                    onMouseDown={handleMouseDown}
                    onTouchStart={handleTouchStart}
                    aria-hidden="true"
                >
                    <div className="thumb-dots">
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                        <div className="dot"></div>
                    </div>
                </div>
                <div className="age-display">
                    <div className="age-input">
                        <input
                            type="text"
                            inputMode="numeric"
                            pattern="[0-9]*"
                            value={inputValue}
                            onChange={handleInputChange}
                            onBlur={handleInputBlur}
                            onFocus={handleInputFocus}
                            onKeyDown={handleInputKeyDown}
                            className="age-input-field"
                            maxLength={2}
                            aria-label="הזן גיל בין 40 ל-80"
                            aria-valuemin={40}
                            aria-valuemax={80}
                        />
                    </div>
                </div>
            </div>
            <div className="slider-labels" aria-hidden="true">
                {[40, 50, 60, 70, 80].map((age) => (
                    <span key={age}>{age}</span>
                ))}
            </div>
        </div>
    );
};

export default AgeSlider;
